import { ReactNode, useState } from 'react';
import {
  Image,
  StyleSheet,
  View,
  type ImageStyle,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { SvgUri } from 'react-native-svg';

import { colors } from '@/constants/theme';

export type BrandLogoProps = {
  /** Absolute URL to the brand mark. SVG and raster both supported. */
  url?: string | null;
  /** Size of the rounded tile in points. Default 40. */
  size?: number;
  /** Tile corner radius. Default 12 (matches `BrandTile` per brand kit). */
  radius?: number;
  /** Padding around the logo inside the tile. Default 6 — keeps the mark off the edges. */
  inset?: number;
  /** Tile background color. Default `colors.frost` (handles dark + light brand marks). */
  background?: string;
  /** Rendered when `url` is missing or a raster image fails to load. */
  fallback: ReactNode;
  style?: StyleProp<ViewStyle>;
  testID?: string;
};

/**
 * Renders a brand's official mark in the canonical 12pt-radius tile,
 * picking SVG vs raster from the URL extension. SVGs use `react-native-svg`'s
 * `SvgUri` (preserves aspect ratio inside the tile); raster URLs use the
 * standard `Image` with `resizeMode='contain'` so wide logos like SoFi's
 * don't get squished. Both paths share an `onError` → fallback pattern
 * so a missing/corrupt asset degrades to the caller-provided fallback
 * (typically an `Avatar` with the brand initials) instead of leaving an
 * empty tile.
 */
export function BrandLogo({
  url,
  size = 40,
  radius = 12,
  inset = 6,
  background = colors.frost,
  fallback,
  style,
  testID,
}: BrandLogoProps) {
  const [loadFailed, setLoadFailed] = useState(false);

  const showFallback = !url || loadFailed;
  if (showFallback) {
    return <>{fallback}</>;
  }

  const isSvg = /\.svg(\?|$)/i.test(url);
  const tileStyle: StyleProp<ViewStyle> = [
    styles.tile,
    {
      width: size,
      height: size,
      borderRadius: radius,
      backgroundColor: background,
      padding: inset,
    },
    style,
  ];

  if (isSvg) {
    const innerSize = size - inset * 2;
    return (
      <View style={tileStyle} testID={testID}>
        <SvgUri
          uri={url}
          width={innerSize}
          height={innerSize}
          onError={() => setLoadFailed(true)}
        />
      </View>
    );
  }

  const imageStyle: ImageStyle = {
    width: size - inset * 2,
    height: size - inset * 2,
  };
  return (
    <View style={tileStyle} testID={testID}>
      <Image
        source={{ uri: url }}
        style={imageStyle}
        resizeMode="contain"
        onError={() => setLoadFailed(true)}
        accessibilityIgnoresInvertColors
      />
    </View>
  );
}

const styles = StyleSheet.create({
  tile: {
    alignItems: 'center',
    justifyContent: 'center',
  },
});
